import React from 'react';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SalovareSelectField from '../common/SalovareSelectField';
import { FIELD_PLACEHOLDER_COLOR } from '../common/formFieldStyles';
import { BRAND, BRAND_RADIUS, brandAlpha } from '../../theme/brandTokens';
import {
  WAITLIST_NAMED_WINDOWS,
  WAITLIST_PRESET_ANY,
  WAITLIST_PRESETS,
  applyWaitlistPreset,
  buildWaitlistClockOptions,
  findWaitlistWindow,
  matchWaitlistPreset,
  normalizeWaitlistTime,
} from '../../utils/waitlistWindowUtils';

const presetChipSx = (selected) => ({
  textTransform: 'none',
  borderRadius: BRAND_RADIUS.pill,
  fontWeight: 600,
  minHeight: 40,
  px: { xs: 1, sm: 1.75 },
  width: { xs: '100%', sm: 'auto' },
  minWidth: 0,
  whiteSpace: 'nowrap',
  border: '1px solid',
  borderColor: selected ? BRAND.accent : BRAND.line,
  bgcolor: selected ? BRAND.accent : BRAND.surface,
  color: selected ? BRAND.onAccent : BRAND.text,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: selected ? BRAND.accentDark : brandAlpha('accent', 0.08),
    borderColor: BRAND.accent,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    borderColor: BRAND.lineSoft,
    color: BRAND.textSubtle,
    bgcolor: BRAND.background,
  },
});

const WaitlistTimePreferenceFields = ({
  windows,
  preset,
  from,
  to,
  disabled,
  onChange,
}) => {
  const { t } = useTranslation();
  const clockOptions = buildWaitlistClockOptions(windows);
  const fromValue = normalizeWaitlistTime(from);
  const toValue = normalizeWaitlistTime(to);
  const fromOptions = clockOptions.filter((time) => !toValue || time < toValue);
  const toOptions = clockOptions.filter((time) => !fromValue || time > fromValue);

  const emitTimes = (nextFrom, nextTo) => {
    onChange({
      from: nextFrom,
      to: nextTo,
      preset: matchWaitlistPreset(nextFrom, nextTo, windows),
    });
  };

  const renderPlaceholder = (labelKey) => (
    <Typography sx={{ color: FIELD_PLACEHOLDER_COLOR }}>
      {t(labelKey)}
    </Typography>
  );

  return (
    <Box sx={{ display: 'grid', gap: 1.5, minWidth: 0 }}>
      <Box
        sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: '1fr 1fr',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {WAITLIST_PRESETS.map((key) => {
          const namedUnavailable = WAITLIST_NAMED_WINDOWS.includes(key) && !findWaitlistWindow(windows, key);
          const selected = preset === key;
          return (
            <Button
              key={key}
              type="button"
              disableElevation
              disabled={disabled || namedUnavailable}
              onClick={() => onChange(applyWaitlistPreset(key, windows))}
              sx={presetChipSx(selected)}
            >
              {t(key === WAITLIST_PRESET_ANY
                ? 'customer.appointments.booking.waitlist.any'
                : `customer.appointments.booking.${key}`)}
            </Button>
          );
        })}
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: { xs: 1, sm: 1.5 },
          minWidth: 0,
        }}
      >
        <SalovareSelectField
          label={t('customer.appointments.booking.waitlist.from')}
          value={fromValue}
          displayEmpty
          disabled={disabled || clockOptions.length === 0}
          onChange={(event) => emitTimes(event.target.value, toValue)}
          renderValue={(value) => (value ? normalizeWaitlistTime(value) : renderPlaceholder('customer.appointments.booking.waitlist.from'))}
          MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
          sx={{ minWidth: 0 }}
        >
          {fromOptions.map((time) => (
            <MenuItem key={`from-${time}`} value={time}>{time}</MenuItem>
          ))}
        </SalovareSelectField>
        <SalovareSelectField
          label={t('customer.appointments.booking.waitlist.to')}
          value={toValue}
          displayEmpty
          disabled={disabled || clockOptions.length === 0}
          onChange={(event) => emitTimes(fromValue, event.target.value)}
          renderValue={(value) => (value ? normalizeWaitlistTime(value) : renderPlaceholder('customer.appointments.booking.waitlist.to'))}
          MenuProps={{ PaperProps: { sx: { maxHeight: 280 } } }}
          sx={{ minWidth: 0 }}
        >
          {toOptions.map((time) => (
            <MenuItem key={`to-${time}`} value={time}>{time}</MenuItem>
          ))}
        </SalovareSelectField>
      </Box>
    </Box>
  );
};

export default WaitlistTimePreferenceFields;
